import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './TeamCompSnapshot.css';
import { Role } from './TeamCompBuilds';
import { SimplifiedChampion } from '../../XXX/gameTypes';
import { GameAssetsProvider } from '../../XXX/context/GameAssetContext';
import StaticChampionCard from '../../StaticSandbox/components/StaticChampionCard';
import DataService from '../../XXX/DataService';

// ═══════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════

export interface SnapshotChampionData {
  championId: string;      // Riot champion ID (e.g., "Aatrox")
  championName: string;    // Display name
  role: Role;
  team: 'blue' | 'red';
  level: number;
  items: string[];         // item IDs at this timestamp
}

interface SnapshotTimestamp {
  label: string;
  sub: string;
  sec: number;
}

interface SnapshotProps {
  timestamp: SnapshotTimestamp;
  champions: SnapshotChampionData[];
  apiVersion: string;
  onClose: () => void;
}

/** One directional matchup: attacker → defender */
interface MatchupResult {
  bestDamage: number;
  bestComboName: string;
  targetMaxHP: number;
}

/** A cell in the 5×5 table: blue attacks red + red attacks blue */
interface CellData {
  blueAttacks: MatchupResult | null;
  redAttacks: MatchupResult | null;
  loading: boolean;
}

const ROLE_LABELS: Record<Role, string> = {
  top: 'Top', jgl: 'Jgl', mid: 'Mid', bot: 'Bot', sup: 'Sup',
};

// ═══════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════

const TeamCompSnapshot: React.FC<SnapshotProps> = ({
  timestamp,
  champions,
  apiVersion,
  onClose,
}) => {
  // ── Backend champion state ──
  const [backendChamps, setBackendChamps] = useState<Record<string, SimplifiedChampion>>({});
  const [initStatus, setInitStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [initError, setInitError] = useState<string | null>(null);

  // ── 5×5 matchup data: key = "blue-{role}-vs-red-{role}" ──
  const [cellData, setCellData] = useState<Record<string, CellData>>({});

  // ── Selected cell for detailed StaticChampionCard view ──
  const [selectedCell, setSelectedCell] = useState<{ blueRole: Role; redRole: Role } | null>(null);

  // ── Helpers ──
  const blueChamps = useMemo(() => champions.filter(c => c.team === 'blue'), [champions]);
  const redChamps = useMemo(() => champions.filter(c => c.team === 'red'), [champions]);
  const champKey = useCallback((c: SnapshotChampionData) => `${c.team}-${c.role}`, []);

  const champImg = useCallback(
    (id: string) => `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${id}.png`,
    [apiVersion],
  );

  const itemImg = useCallback(
    (id: string) => `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${id}.png`,
    [apiVersion],
  );

  const cellKey = (blueRole: Role, redRole: Role) => `blue-${blueRole}-vs-red-${redRole}`;

  // ── Initialize all champions on backend ──
  useEffect(() => {
    if (champions.length === 0) return;

    let cancelled = false;
    const createAll = async () => {
      setInitStatus('loading');
      setInitError(null);
      setCellData({});
      setSelectedCell(null);

      const base = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      try {
        const results = await Promise.all(
          champions.map(async champ => {
            const resp = await fetch(`${base}/api/static/champion/create`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                championName: champ.championId,
                level: champ.level,
                team: champ.team,
                items: champ.items,
              }),
            });
            if (!resp.ok) throw new Error(`Failed to create ${champ.championName}`);
            const data = await resp.json();
            if (!data.success) throw new Error(data.error || `Failed: ${champ.championName}`);
            return { key: champKey(champ), champion: data.data as SimplifiedChampion };
          }),
        );

        if (cancelled) return;

        const map: Record<string, SimplifiedChampion> = {};
        results.forEach(r => { map[r.key] = r.champion; });
        setBackendChamps(map);
        setInitStatus('ready');
      } catch (err) {
        if (cancelled) return;
        console.error('[TeamCompSnapshot] Init error:', err);
        setInitStatus('error');
        setInitError(err instanceof Error ? err.message : 'Unknown error');
      }
    };

    createAll();
    return () => { cancelled = true; };
  }, [champions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch ALL 25 matchup pairs once champions are ready ──
  useEffect(() => {
    if (initStatus !== 'ready') return;
    if (blueChamps.length === 0 || redChamps.length === 0) return;

    // For each blue×red pair, fetch both directions
    blueChamps.forEach(blueC => {
      const blueBackend = backendChamps[champKey(blueC)];
      if (!blueBackend) return;

      redChamps.forEach(redC => {
        const redBackend = backendChamps[champKey(redC)];
        if (!redBackend) return;

        const key = cellKey(blueC.role, redC.role);
        if (cellData[key]) return; // already fetched or fetching

        setCellData(prev => ({
          ...prev,
          [key]: { blueAttacks: null, redAttacks: null, loading: true },
        }));

        Promise.all([
          DataService.fetchStaticMatchupCombos(blueBackend.id, redBackend.id).catch(() => null),
          DataService.fetchStaticMatchupCombos(redBackend.id, blueBackend.id).catch(() => null),
        ]).then(([blueOffData, redOffData]: [any, any]) => {
          const blueCombos: any[] = blueOffData?.mainChampion?.combos || [];
          const redCombos: any[] = redOffData?.mainChampion?.combos || [];

          const bestBlue = blueCombos.reduce(
            (best: any, c: any) => (c.damage > (best?.damage || 0) ? c : best), null,
          );
          const bestRed = redCombos.reduce(
            (best: any, c: any) => (c.damage > (best?.damage || 0) ? c : best), null,
          );

          setCellData(prev => ({
            ...prev,
            [key]: {
              blueAttacks: bestBlue ? {
                bestDamage: bestBlue.damage,
                bestComboName: bestBlue.combo?.name || bestBlue.combo?.sequence || '-',
                targetMaxHP: bestBlue.targetMaxHealth || 0,
              } : null,
              redAttacks: bestRed ? {
                bestDamage: bestRed.damage,
                bestComboName: bestRed.combo?.name || bestRed.combo?.sequence || '-',
                targetMaxHP: bestRed.targetMaxHealth || 0,
              } : null,
              loading: false,
            },
          }));
        });
      });
    });
  }, [initStatus, backendChamps]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Champion update handler (for StaticChampionCard) ──
  const handleChampionUpdate = useCallback((updated: SimplifiedChampion) => {
    setBackendChamps(prev => {
      const next = { ...prev };
      for (const [key, champ] of Object.entries(next)) {
        if (champ.id === updated.id) {
          next[key] = updated;
          break;
        }
      }
      return next;
    });
  }, []);

  // ── Derived: selected champions for the card view ──
  const selectedBlue = selectedCell ? backendChamps[`blue-${selectedCell.blueRole}`] : null;
  const selectedRed = selectedCell ? backendChamps[`red-${selectedCell.redRole}`] : null;

  // Damage percentage helper
  const dmgPct = (dmg: number, maxHP: number) =>
    maxHP > 0 ? Math.round((dmg / maxHP) * 100) : 0;

  // Color class for a cell based on who wins the trade
  const cellTintClass = (cell: CellData): string => {
    if (!cell.blueAttacks || !cell.redAttacks) return '';
    const bluePct = dmgPct(cell.blueAttacks.bestDamage, cell.blueAttacks.targetMaxHP);
    const redPct = dmgPct(cell.redAttacks.bestDamage, cell.redAttacks.targetMaxHP);
    const diff = bluePct - redPct;
    if (diff > 20) return 'cell-blue-favored';
    if (diff < -20) return 'cell-red-favored';
    return 'cell-even';
  };

  // ═══════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="tcb-snapshot-section">
      {/* ── Header ── */}
      <div className="tcb-snapshot-header">
        <h3 className="tcb-snapshot-title">
          ⚔ Snapshot — {timestamp.label} ({timestamp.sub})
        </h3>
        <button className="tcb-snapshot-close" onClick={onClose} title="Close snapshot">
          ✕
        </button>
      </div>

      {/* ── Loading ── */}
      {initStatus === 'loading' && (
        <div className="tcb-snapshot-status loading">
          <div className="tcb-snapshot-spinner" />
          <p>Initializing {champions.length} champions…</p>
        </div>
      )}

      {/* ── Error ── */}
      {initStatus === 'error' && (
        <div className="tcb-snapshot-status error">
          <p>Failed to initialize champions: {initError}</p>
          <button className="tcb-snapshot-retry-btn" onClick={onClose}>Dismiss</button>
        </div>
      )}

      {/* ── Ready — 5×5 matchup table ── */}
      {initStatus === 'ready' && (
        <>
          <p className="tcb-snapshot-hint">
            Rows = Blue team attacking → Columns = Red team defending · Click a cell for detailed view
          </p>

          <div className="tcb-matrix-wrap">
            <table className="tcb-matrix-table">
              <thead>
                <tr>
                  {/* Corner cell: label */}
                  <th className="tcb-matrix-corner">
                    <span className="tcb-matrix-corner-blue">Blue ⚔</span>
                    <span className="tcb-matrix-corner-red">vs Red 🛡</span>
                  </th>
                  {/* Red team column headers */}
                  {redChamps.map(rc => (
                    <th key={rc.role} className="tcb-matrix-col-header">
                      <img src={champImg(rc.championId)} alt={rc.championName} />
                      <span className="tcb-matrix-col-name">{rc.championName}</span>
                      <span className="tcb-matrix-col-meta">
                        {ROLE_LABELS[rc.role]} · Lv{rc.level}
                      </span>
                      <div className="tcb-matrix-col-items">
                        {rc.items.map((id, idx) => (
                          <img key={idx} src={itemImg(id)} alt="" />
                        ))}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {blueChamps.map(bc => (
                  <tr key={bc.role}>
                    {/* Blue team row header */}
                    <td className="tcb-matrix-row-header">
                      <img src={champImg(bc.championId)} alt={bc.championName} />
                      <div className="tcb-matrix-row-info">
                        <span className="tcb-matrix-row-name">{bc.championName}</span>
                        <span className="tcb-matrix-row-meta">
                          {ROLE_LABELS[bc.role]} · Lv{bc.level}
                        </span>
                        <div className="tcb-matrix-row-items">
                          {bc.items.map((id, idx) => (
                            <img key={idx} src={itemImg(id)} alt="" />
                          ))}
                        </div>
                      </div>
                    </td>

                    {/* Matchup cells */}
                    {redChamps.map(rc => {
                      const key = cellKey(bc.role, rc.role);
                      const cell = cellData[key];
                      const isSelected =
                        selectedCell?.blueRole === bc.role &&
                        selectedCell?.redRole === rc.role;

                      return (
                        <td
                          key={rc.role}
                          className={`tcb-matrix-cell ${cell && !cell.loading ? cellTintClass(cell) : ''} ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelectedCell({ blueRole: bc.role, redRole: rc.role })}
                        >
                          {(!cell || cell.loading) && (
                            <div className="tcb-matrix-cell-loading">…</div>
                          )}
                          {cell && !cell.loading && (
                            <div className="tcb-matrix-cell-content">
                              {/* Blue attacking Red */}
                              <div className="tcb-matrix-cell-row off">
                                <span className="tcb-matrix-cell-icon">⚔</span>
                                <span className="tcb-matrix-cell-dmg">
                                  {cell.blueAttacks ? Math.round(cell.blueAttacks.bestDamage) : '—'}
                                </span>
                                {cell.blueAttacks && cell.blueAttacks.targetMaxHP > 0 && (
                                  <span className="tcb-matrix-cell-pct">
                                    {dmgPct(cell.blueAttacks.bestDamage, cell.blueAttacks.targetMaxHP)}%
                                  </span>
                                )}
                              </div>
                              {/* Red attacking Blue */}
                              <div className="tcb-matrix-cell-row def">
                                <span className="tcb-matrix-cell-icon">🛡</span>
                                <span className="tcb-matrix-cell-dmg">
                                  {cell.redAttacks ? Math.round(cell.redAttacks.bestDamage) : '—'}
                                </span>
                                {cell.redAttacks && cell.redAttacks.targetMaxHP > 0 && (
                                  <span className="tcb-matrix-cell-pct">
                                    {dmgPct(cell.redAttacks.bestDamage, cell.redAttacks.targetMaxHP)}%
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Detailed StaticChampionCard view for selected cell ── */}
          {selectedBlue && selectedRed && (
            <GameAssetsProvider>
              <div className="tcb-snapshot-card-wrap">
                <h4 className="tcb-snapshot-card-title">
                  {selectedBlue.name} vs {selectedRed.name}
                </h4>
                <div className="tcb-snapshot-cards-row">
                  <div className="tcb-snapshot-card-col">
                    <StaticChampionCard
                      key={`focus-${selectedBlue.id}`}
                      champion={selectedBlue}
                      targetChampion={selectedRed}
                      onChampionUpdate={handleChampionUpdate}
                      defaultShowCombos={true}
                    />
                  </div>
                  <div className="tcb-snapshot-card-col">
                    <StaticChampionCard
                      key={`target-${selectedRed.id}`}
                      champion={selectedRed}
                      targetChampion={selectedBlue}
                      onChampionUpdate={handleChampionUpdate}
                      defaultShowCombos={true}
                    />
                  </div>
                </div>
              </div>
            </GameAssetsProvider>
          )}
        </>
      )}
    </div>
  );
};

export default TeamCompSnapshot;
